import { useFeedback, useTranslation } from "@opendash/core";
import { Icon } from "@opendash/icons";
import { Modal, Upload } from "antd";
import { RcFile, UploadProps } from "antd/es/upload";
import type { UploadFile } from "antd/es/upload/interface";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import React, { CSSProperties, useEffect, useState } from "react";

/**
 * @description You need to have a state where references to files can be stored and pass the setter as prop.<br><br>Add something like this "const [parseFiles, setParseFiles] = useState<Parse.File[]>([]);" to your component and provide setParseFiles as argument in props.
 * @param props props is of type {Parse.File[], React.Dispatch<React.SetStateAction<Parse.File[]>>}
 * @returns Sets the references to the files in parse in your state.
 */
export const SelectMedia = observer(
  (props: {
    parseFiles: Parse.File[];
    setParseFiles: React.Dispatch<React.SetStateAction<Parse.File[]>>;
    style?: CSSProperties;
  }) => {
    const t = useTranslation();
    const { message } = useFeedback();
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState("");
    const [previewTitle, setPreviewTitle] = useState("");
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [temporaryUniqueName, setTemporaryUniqueName] = useState("");
    const getBase64 = (file: RcFile): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
      });
    };

    const getFileEnding = (filename: string) => {
      return filename.slice(filename.lastIndexOf("."), filename.length);
    };

    const onCancel = () => setPreviewOpen(false);

    const uploadprops: UploadProps = {
      listType: "picture-card",
      fileList: fileList,
      name: temporaryUniqueName,
      beforeUpload: (file) => {
        const isallowed =
          file.type === "image/png" ||
          file.type === "image/jpeg" ||
          file.type === "image/jpg";
        if (!isallowed) {
          message.error(`${file.name} is not allowed.`);
        }
        setTemporaryUniqueName(crypto.randomUUID() + getFileEnding(file.name));
        return isallowed || Upload.LIST_IGNORE;
      },
      onPreview: async (file: UploadFile) => {
        if (!file.url && !file.preview) {
          file.preview = await getBase64(file.originFileObj as RcFile);
        }
        setPreviewImage(file.url || (file.preview as string));
        setPreviewOpen(true);
        setPreviewTitle(
          file.name || file.url!.substring(file.url!.lastIndexOf("/") + 1)
        );
      },
      onChange: ({ file, fileList: newFileList }) => {
        file.name = temporaryUniqueName;
        // file.uid = temporaryUniqueName.slice(
        //   0,
        //   temporaryUniqueName.lastIndexOf(".") - 1
        // );
        setFileList(newFileList);
      },
      onRemove: async (file: UploadFile) => {
        const filetoremove: any = props.parseFiles.find(
          (parsefile: Parse.File) => {
            return parsefile.name().endsWith(file.name);
          }
        );
        if (filetoremove) {
          const files = [...props.parseFiles];
          // TODO: Delete file by calling cloud code
          // await filetoremove.destroy();
          files.splice(files.indexOf(filetoremove), 1);
          props.setParseFiles([...files]);
          return true;
        }
        return false;
      },
      customRequest: async ({ filename, file, onSuccess, onError }) => {
        const newMediaFile: Parse.File = new Parse.File(filename as string, {
          base64: await getBase64(file as RcFile),
        });
        newMediaFile.save().then(
          (savedfile: Parse.File) => {
            // The file has been saved to Parse.
            props.setParseFiles(
              props.parseFiles?.concat([savedfile]) ?? [savedfile]
            );
            if (onSuccess !== undefined) {
              onSuccess("ok");
            }
          },
          (error) => {
            // The file either could not be read, or could not be saved to Parse.
            if (onError) {
              onError(error);
            }
          }
        );
      },
    };

    useEffect(() => {
      if (props.parseFiles?.length > 0) {
        const mappedfiles = props.parseFiles.map((file: Parse.File) => {
          const uploadfile: UploadFile = {
            uid: file.name().split(".")[0],
            name: file.name(),
            url: file.url(),
            status: "done",
          };
          return uploadfile;
        });
        setFileList(mappedfiles);
      } else {
        setFileList([]);
      }
    }, [props.parseFiles]);

    const uploadButton = (
      <div>
        <Icon icon="fa:plus" />
        <div style={{ marginTop: 8 }}>
          {t("maintenance:media.select.form.button.label")}
        </div>
      </div>
    );

    return (
      <div style={props.style}>
        <Upload {...uploadprops} style={props.style}>
          {fileList.length >= 8 ? null : uploadButton}
        </Upload>
        <Modal
          open={previewOpen}
          title={previewTitle}
          footer={null}
          onCancel={onCancel}
        >
          <img
            alt="articleimage"
            style={{ width: "100%" }}
            src={previewImage}
          />
        </Modal>
      </div>
    );
  }
);
